/**
 * Customer Portal (UI) - typed API client for Pages Function endpoints.
 *
 * Current behavior:
 * - Calls mocked endpoints.
 *
 * Future behavior:
 * - Same functions remain, while backend implementation behind `/api/*` swaps
 *   from mock data to service bindings.
 */
import type {
  AccountContextApiResponse,
  CallbackProfileUpsertApiResponse,
  CallbackProfileUpsertInput,
  Brand,
  BrandsApiResponse,
  BillingApiResponse,
  Campaign,
  CallbackProfilesApiResponse,
  CampaignsApiResponse,
  DashboardApiResponse,
  PhoneNumber,
  PhoneNumbersApiResponse,
  OpenSupportTicketsApiResponse,
  PortalFilters,
  ScopeType,
  SessionApiResponse,
  RetryWebhookJobApiResponse,
  SupportTicketSubmitApiResponse,
  SupportTicketSubmitInput,
  WebhookJobDetailApiResponse,
  WebhookAuditReportApiResponse,
  WebhookAuditReportQuery,
  TemplateUpsertApiResponse,
  TemplateUpsertInput,
  TemplateItem,
  TemplatesApiResponse,
  TemplateAssignmentListApiResponse,
  TemplateAssignmentsSyncApiResponse,
  TemplateAssignmentsSyncInput,
  TemplateAssignmentContextApiResponse,
  WebhookTypesApiResponse,
  WebhookEventAssignmentUpsertApiResponse,
  WebhookEventAssignmentUpsertInput,
  WebhookEventAssignmentsApiResponse,
  WebhookTypeId
} from '../../types/portal';

type ApiEnvelope<T> = {
  SchemaVersion: string;
  RequestedAt: string;
  Data: T;
};

function ensureObjectRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Contract violation: ${label} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function ensureArrayField<T>(value: unknown, label: string): T[] {
  if (!Array.isArray(value)) {
    throw new Error(`Contract violation: ${label} must be an array.`);
  }
  return value as T[];
}

function unwrapEnvelope<T>(value: unknown): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value as T;
  }
  const record = value as Record<string, unknown>;
  if (
    typeof record.SchemaVersion === 'string' &&
    typeof record.RequestedAt === 'string' &&
    Object.prototype.hasOwnProperty.call(record, 'Data')
  ) {
    return (record as ApiEnvelope<T>).Data;
  }
  return value as T;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, path));
  }
  return unwrapEnvelope<T>(await response.json());
}

async function sendJson<T>(path: string, method: 'POST' | 'PUT' | 'DELETE', body: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, path));
  }
  return unwrapEnvelope<T>(await response.json());
}

async function extractApiErrorMessage(response: Response, path: string): Promise<string> {
  try {
    const payload = (await response.json()) as {
      Errors?: Array<{ Message?: unknown }>;
      message?: unknown;
      error?: unknown;
    };
    const firstErrorMessage = payload.Errors?.[0]?.Message;
    if (typeof firstErrorMessage === 'string' && firstErrorMessage.trim()) {
      return firstErrorMessage.trim();
    }
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }
    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error.trim();
    }
  } catch {
    // Fall back to generic status text below.
  }
  return `Request failed (${response.status}) for ${path}`;
}

function normalizePhoneScopeId(scopeType: ScopeType, scopeId: string): string {
  if (scopeType !== 'PHONE') {
    return scopeId.trim();
  }
  const digitsOnly = scopeId.replace(/\D/g, '');
  return digitsOnly.length > 0 ? digitsOnly : scopeId.trim();
}

export async function fetchSession(): Promise<SessionApiResponse> {
  return getJson<SessionApiResponse>('/api/session');
}

export async function fetchAccountContext(): Promise<AccountContextApiResponse> {
  return getJson<AccountContextApiResponse>('/api/account-context');
}

export async function setActiveOrganization(organizationId: string): Promise<SessionApiResponse> {
  return sendJson<SessionApiResponse>('/api/session/active-organization', 'POST', { organizationId });
}

export async function fetchCampaigns(accountId: string): Promise<CampaignsApiResponse> {
  const payload = await getJson<unknown>(`/api/campaigns?accountId=${encodeURIComponent(accountId)}`);
  const record = ensureObjectRecord(payload, 'Campaigns response');
  return { items: ensureArrayField<Campaign>(record.items, 'Campaigns items') };
}

export async function fetchBrands(accountId: string): Promise<BrandsApiResponse> {
  const payload = await getJson<unknown>(`/api/brands?accountId=${encodeURIComponent(accountId)}`);
  const record = ensureObjectRecord(payload, 'Brands response');
  return { items: ensureArrayField<Brand>(record.items, 'Brands items') };
}

export async function fetchPhoneNumbers(
  accountId: string,
  filters: PortalFilters
): Promise<PhoneNumbersApiResponse> {
  const query = new URLSearchParams({ accountId });
  if (filters.brandId) {
    query.set('brandId', filters.brandId);
  }
  if (filters.campaignId) {
    query.set('campaignId', filters.campaignId);
  }
  const payload = await getJson<unknown>(`/api/phone-numbers?${query.toString()}`);
  const record = ensureObjectRecord(payload, 'Phone numbers response');
  return { items: ensureArrayField<PhoneNumber>(record.items, 'Phone numbers items') };
}

export async function fetchDashboard(
  accountId: string,
  filters: PortalFilters,
  range?: { startIso?: string | null; endIso?: string | null; timeZone?: string | null }
): Promise<DashboardApiResponse> {
  const query = new URLSearchParams({ accountId });
  if (filters.brandId) query.set('brandId', filters.brandId);
  if (filters.campaignId) query.set('campaignId', filters.campaignId);
  if (filters.phoneNumberId) query.set('phoneNumberId', filters.phoneNumberId);
  if (range?.startIso) query.set('startIso', range.startIso);
  if (range?.endIso) query.set('endIso', range.endIso);
  if (range?.timeZone) query.set('timeZone', range.timeZone);
  const payload = await getJson<unknown>(`/api/dashboard?${query.toString()}`);
  const payloadRecord = ensureObjectRecord(payload, 'Dashboard response');
  const dashboardRecord = ensureObjectRecord(payloadRecord.Dashboard, 'Dashboard');
  ensureObjectRecord(dashboardRecord.range, 'Dashboard.range');
  ensureArrayField(dashboardRecord.metrics, 'Dashboard.metrics');
  ensureArrayField(dashboardRecord.series, 'Dashboard.series');
  return { dashboard: dashboardRecord as unknown as DashboardApiResponse['dashboard'] };
}

export async function fetchBilling(
  accountId: string,
  filters: PortalFilters,
  range?: { startIso?: string | null; endIso?: string | null; timeZone?: string | null }
): Promise<BillingApiResponse> {
  const query = new URLSearchParams({ accountId });
  if (filters.brandId) query.set('brandId', filters.brandId);
  if (filters.campaignId) query.set('campaignId', filters.campaignId);
  if (filters.phoneNumberId) query.set('phoneNumberId', filters.phoneNumberId);
  if (range?.startIso) query.set('startIso', range.startIso);
  if (range?.endIso) query.set('endIso', range.endIso);
  if (range?.timeZone) query.set('timeZone', range.timeZone);
  const payload = await getJson<unknown>(`/api/billing?${query.toString()}`);
  const payloadRecord = ensureObjectRecord(payload, 'Billing response');
  const billingRecord = ensureObjectRecord(payloadRecord.Billing, 'Billing');
  ensureObjectRecord(billingRecord.range, 'Billing.range');
  ensureObjectRecord(billingRecord.summary, 'Billing.summary');
  ensureArrayField(billingRecord.pricing, 'Billing.pricing');
  return { billing: billingRecord as unknown as BillingApiResponse['billing'] };
}

export async function fetchCallbackProfiles(accountId: string): Promise<CallbackProfilesApiResponse> {
  return getJson<CallbackProfilesApiResponse>(`/api/webhooks/callback-profiles?accountId=${encodeURIComponent(accountId)}`);
}

export async function upsertCallbackProfile(
  accountId: string,
  input: CallbackProfileUpsertInput
): Promise<CallbackProfileUpsertApiResponse> {
  return sendJson<CallbackProfileUpsertApiResponse>(
    `/api/webhooks/callback-profiles?accountId=${encodeURIComponent(accountId)}`,
    'POST',
    input
  );
}

export async function deleteCallbackProfile(accountId: string, profileId: string): Promise<{ ok: true }> {
  return sendJson<{ ok: true }>(
    `/api/webhooks/callback-profiles?accountId=${encodeURIComponent(accountId)}`,
    'DELETE',
    { profileId }
  );
}

export async function fetchWebhookEventAssignments(
  accountId: string,
  filters: { webhookType?: WebhookTypeId; scopeType?: ScopeType; scopeId?: string } = {}
): Promise<WebhookEventAssignmentsApiResponse> {
  const query = new URLSearchParams({ accountId });
  if (filters.webhookType) query.set('webhookType', filters.webhookType);
  if (filters.scopeType) query.set('scopeType', filters.scopeType);
  if (filters.scopeId) query.set('scopeId', filters.scopeId);
  return getJson<WebhookEventAssignmentsApiResponse>(`/api/webhooks/event-assignments?${query.toString()}`);
}

export async function fetchWebhookTypes(): Promise<WebhookTypesApiResponse> {
  return getJson<WebhookTypesApiResponse>('/api/webhooks/types');
}

export async function upsertWebhookEventAssignment(
  accountId: string,
  input: WebhookEventAssignmentUpsertInput
): Promise<WebhookEventAssignmentUpsertApiResponse> {
  return sendJson<WebhookEventAssignmentUpsertApiResponse>(
    `/api/webhooks/event-assignments?accountId=${encodeURIComponent(accountId)}`,
    'POST',
    {
      ...input,
      scopeId: normalizePhoneScopeId(input.scopeType, input.scopeId)
    }
  );
}

export async function deleteWebhookEventAssignment(
  accountId: string,
  input: { assignmentId?: string; webhookType?: WebhookTypeId; scopeType?: ScopeType; scopeId?: string; profileId?: string }
): Promise<{ ok: true }> {
  return sendJson<{ ok: true }>(
    `/api/webhooks/event-assignments?accountId=${encodeURIComponent(accountId)}`,
    'DELETE',
    {
      ...input,
      scopeId:
        input.scopeType && input.scopeId
          ? normalizePhoneScopeId(input.scopeType, input.scopeId)
          : input.scopeId
    }
  );
}

export async function fetchTemplates(accountId?: string | null): Promise<TemplatesApiResponse> {
  const query = new URLSearchParams();
  if (typeof accountId === 'string' && accountId.trim().length > 0) {
    query.set('accountId', accountId.trim());
  }
  const path = query.size > 0 ? `/api/templates?${query.toString()}` : '/api/templates';
  const payload = await getJson<unknown>(path);
  const payloadRecord = ensureObjectRecord(payload, 'Templates response');
  return { items: ensureArrayField<TemplateItem>(payloadRecord.Items, 'Templates Items') };
}

export async function upsertTemplate(input: TemplateUpsertInput): Promise<TemplateUpsertApiResponse> {
  const payload = await sendJson<{ TemplateId: string }>('/api/templates', 'POST', input);
  return { ok: true, templateId: payload.TemplateId };
}

export async function deleteTemplate(templateKey: string): Promise<{ ok: true }> {
  await sendJson<{ Deleted: boolean }>('/api/templates', 'DELETE', { templateKey });
  return { ok: true };
}

export async function fetchTemplateAssignments(
  scopeType: ScopeType,
  scopeId: string
): Promise<TemplateAssignmentListApiResponse> {
  const query = new URLSearchParams({ scopeType, scopeId });
  const payload = await getJson<{
    AccountId: string;
    ScopeType: ScopeType;
    ScopeId: string;
    Items: TemplateAssignmentListApiResponse['items'];
  }>(`/api/template-assignments?${query.toString()}`);
  return {
    accountId: payload.AccountId,
    scopeType: payload.ScopeType,
    scopeId: payload.ScopeId,
    items: payload.Items
  };
}

export async function syncTemplateAssignments(
  input: TemplateAssignmentsSyncInput
): Promise<TemplateAssignmentsSyncApiResponse> {
  const payload = await sendJson<{
    AccountId: string;
    ScopeType: ScopeType;
    ScopeId: string;
    Added: string[];
    Removed: string[];
    Items: TemplateAssignmentsSyncApiResponse['items'];
  }>('/api/template-assignments', 'PUT', input);
  return {
    ok: true,
    accountId: payload.AccountId,
    scopeType: payload.ScopeType,
    scopeId: payload.ScopeId,
    added: payload.Added,
    removed: payload.Removed,
    items: payload.Items
  };
}

export async function fetchTemplateAssignmentContext(
  scopeType: ScopeType,
  scopeId: string,
  campaignId?: string | null
): Promise<TemplateAssignmentContextApiResponse> {
  const query = new URLSearchParams({ scopeType, scopeId });
  if (campaignId) {
    query.set('campaignId', campaignId);
  }
  const payload = await getJson<{
    AccountId: string;
    ScopeType: ScopeType;
    ScopeId: string;
    CampaignId?: string | null;
    AtScope: TemplateAssignmentContextApiResponse['atScope'];
    InheritedAccount: TemplateAssignmentContextApiResponse['inheritedAccount'];
    InheritedCampaign: TemplateAssignmentContextApiResponse['inheritedCampaign'];
    Inherited: TemplateAssignmentContextApiResponse['inherited'];
  }>(`/api/template-assignment-context?${query.toString()}`);
  return {
    accountId: payload.AccountId,
    scopeType: payload.ScopeType,
    scopeId: payload.ScopeId,
    campaignId: payload.CampaignId ?? null,
    atScope: payload.AtScope,
    inheritedAccount: payload.InheritedAccount,
    inheritedCampaign: payload.InheritedCampaign,
    inherited: payload.Inherited
  };
}

export async function fetchWebhookAuditReport(
  query: WebhookAuditReportQuery = {}
): Promise<WebhookAuditReportApiResponse> {
  const params = new URLSearchParams();
  if (query.brandId) params.set('brandId', query.brandId);
  if (query.campaignId) params.set('campaignId', query.campaignId);
  if (query.phoneNumberId) params.set('phoneNumberId', query.phoneNumberId);
  if (query.webhookType) params.set('webhookType', query.webhookType);
  if (query.status) params.set('status', query.status);
  if (query.fromIso) params.set('fromIso', query.fromIso);
  if (query.toIso) params.set('toIso', query.toIso);
  if (typeof query.limit === 'number' && Number.isFinite(query.limit)) {
    params.set('limit', String(Math.floor(query.limit)));
  }
  if (query.cursor) params.set('cursor', query.cursor);
  const suffix = params.toString();
  return getJson<WebhookAuditReportApiResponse>(`/api/reports/webhooks${suffix ? `?${suffix}` : ''}`);
}

export async function fetchWebhookJobDetails(jobId: number, shardName: string): Promise<WebhookJobDetailApiResponse> {
  const path = buildWebhookJobDetailsPath(jobId, shardName);
  if (!path) {
    throw new Error('Invalid webhook job reference.');
  }
  return getJson<WebhookJobDetailApiResponse>(path);
}

export function buildWebhookJobDetailsPath(jobId: number, shardName: string): string | null {
  const normalizedShardName = typeof shardName === 'string' ? shardName.trim() : '';
  const normalizedJobId = Number.isFinite(jobId) ? Math.floor(jobId) : NaN;
  if (!normalizedShardName || !Number.isFinite(normalizedJobId) || normalizedJobId < 1) {
    return null;
  }
  const query = new URLSearchParams({ shardName: normalizedShardName, jobId: String(normalizedJobId) });
  return `/api/reports/webhooks?${query.toString()}`;
}

export async function retryWebhookJob(jobId: number, shardName: string): Promise<RetryWebhookJobApiResponse> {
  return sendJson<RetryWebhookJobApiResponse>(`/api/reports/webhooks`, 'POST', {
    jobId,
    shardName
  });
}

export async function submitSupportTicket(
  accountId: string,
  input: SupportTicketSubmitInput
): Promise<SupportTicketSubmitApiResponse> {
  return sendJson<SupportTicketSubmitApiResponse>(
    `/api/support/tickets?accountId=${encodeURIComponent(accountId)}`,
    'POST',
    input
  );
}

export async function fetchOpenSupportTickets(accountId: string): Promise<OpenSupportTicketsApiResponse> {
  return getJson<OpenSupportTicketsApiResponse>(`/api/support/tickets?accountId=${encodeURIComponent(accountId)}`);
}
